import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AddToTopicModal from '../AddToTopicModal';
import { apiErrorMessage } from '../../services/errorMessage';
import { withReturnTo } from '../common/returnPath';
import type { TopicKeyword, TrendProposal } from '../../types';

/**
 * Turning a proposal into a page, which means first deciding where it lives.
 *
 * A ManagedPage cannot exist without a TopicKeyword — the page builder raises
 * without `topic_keyword.keyword` — so "Create" cannot mint anything in a
 * vacuum. It has to ask where.
 *
 * That question is answered by the SAME modal every other keyword on this page
 * uses. It already picks a subject, picks or creates a node at any depth,
 * creates the subject on the way in when there isn't one, and writes the
 * TopicKeyword rows. Growing a parallel destination picker for trends would be
 * a second thing to keep in step with the subject tree, which is exactly the
 * mistake the spiking-term flow avoided by reusing it.
 *
 * The modal now hands back the TopicKeyword rows it wrote. That mattered enough
 * to widen its callback: looking the row up afterwards by keyword would be a
 * guess the moment the same keyword sits under two nodes, and picking the wrong
 * one files the page in the wrong part of the site.
 */
export default function CreateFromProposal({
  proposal,
  onClose,
  onCreated,
}: {
  proposal: TrendProposal;
  onClose: () => void;
  onCreated: (pageId: number) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiled = async (created?: TopicKeyword[]) => {
    const topicKeywordId = created?.[0]?.id;
    if (!topicKeywordId) {
      setError('That keyword did not land anywhere - try picking a topic again.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.createPageFromProposal(proposal.id, {
        topic_keyword_id: topicKeywordId,
      });
      onCreated(result.managed_page_id);
      // Carries the way back for the same reason Open page does: you have just
      // been moved to a different screen by a button, and the list you were
      // working is where you will want to return.
      navigate(
        withReturnTo(
          `/page-builder/${result.managed_page_id}`,
          `${location.pathname}${location.search}`,
          'Trends',
        ),
      );
    } catch (e) {
      setError(apiErrorMessage(e));
      setBusy(false);
    }
  };

  return (
    <>
      <AddToTopicModal
        keywords={[{ id: proposal.keyword_id, value: proposal.keyword }]}
        // Say what is being made. Without this the dialog reads "Add to a
        // subject" over "Add to Storage", so the click looks like it filed a
        // keyword and the jump to the page builder afterwards is unexplained.
        purpose={{
          title: 'Where should this page live?',
          blurb: `"${proposal.title}" - targeting ${proposal.keyword}. Pick the topic it belongs under, or make one.`,
          verb: 'Create page in',
        }}
        onClose={onClose}
        onSuccess={handleFiled}
      />
      {busy && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
          <div className="rounded-lg bg-white dark:bg-gray-800 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 shadow-lg">
            Building the page…
          </div>
        </div>
      )}
      {error && (
        <div className="fixed bottom-4 right-4 z-[60] max-w-sm rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/40 px-3 py-2 text-sm text-red-700 dark:text-red-300 shadow-lg">
          {error}
        </div>
      )}
    </>
  );
}
